import React, { useEffect, useState } from 'react';
import {
  Tabs,
  Tab,
  Box,
  TextField,
  MenuItem,
  Grid,
  Avatar,
  Typography,
  useMediaQuery,
  Skeleton,
  Badge,
  Stack,
  Button,
} from '@mui/material';
import WinnerCard from './WinnerCard';
import {
  useGetAllEmployeesQuery,
  useGetAllWinnersQuery,
  useGetNominationsByQuarterAndYearQuery,
  useGetPreviouslyClosedQuarterQuery,
  useGetRnRYearsQuery,
} from '../../rnrApiSlice';
import Q1Icon from '@mui/icons-material/LooksOne';
import Q2Icon from '@mui/icons-material/LooksTwo';
import Q3Icon from '@mui/icons-material/Looks3';
import Q4Icon from '@mui/icons-material/Looks4';
import WinnerDialog from './WinnerDialog';
import RnRCardsSkeleton, { LoadingScreen } from './RnRCardsSkeleton';
import Light from '../../assets/light.gif';
import { useTheme } from '@emotion/react';
import { variants } from '../../../../variants/variants';
import linkwaylogo from '../../../../assets/Linkway/LinkwayLogo.png';
import GNXTLogo from '../../../../assets/GatewayLogo/G_NXTLogo.png';
import SmallWinnerCard from './SmallWinnerCard';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Can } from '../../../../context/Ability';
import RnRDrawer from '../../RnRForm/components/RnRDrawer';
import SwipeEdge from '../../../../common/SwipeEdge';

const Winners = () => {
  const [fetchData, setFetchData] = useState(true);
  const { data: previousQuarter, isLoading: prevLoading } = useGetPreviouslyClosedQuarterQuery();
  const [quarters] = useState(['Quarter1', 'Quarter2', 'Quarter3', 'Quarter4']);
  const [selectedWinner, setSelectedWinner] = useState(null);
  const { data: years } = useGetRnRYearsQuery();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState(1);
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const urlElements = pathname.split('/');
  const drawerKey = urlElements[4];
  let drawerOpen = ['comment', 'rnr', 'shabash', 'resume', 'rnrNomination', 'shabashNomination'].includes(drawerKey);
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const { data: allWinners, isLoading: allLoading } = useGetAllWinnersQuery();
  const {
    data: winnerData,
    isLoading,
    error,
    isFetching,
  } = useGetNominationsByQuarterAndYearQuery(
    {
      year: selectedYear,
      quarter: selectedQuarter,
    },
    {
      skip: !fetchData, // Skip the query if fetchData is false
    }
  );

  useEffect(() => {
    if (!prevLoading && previousQuarter?.data) {
      setSelectedQuarter(previousQuarter.data.time.quarter);
    }
  }, [previousQuarter, prevLoading]);

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
    setSelectedQuarter(1);
  };

  const handleQuarterChange = (event, newValue) => {
    setSelectedQuarter(newValue + 1);
    setFetchData(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setSelectedWinner(null);
  };

  const icons = {
    Quarter1: <Q1Icon />,
    Quarter2: <Q2Icon />,
    Quarter3: <Q3Icon />,
    Quarter4: <Q4Icon />,
  };

  const labels = {
    Quarter1: 'Jan - Mar',
    Quarter2: 'Apr - Jun',
    Quarter3: 'Jul - Sep',
    Quarter4: 'Oct - Dec',
  };

  // const getWinnerCount = (quarter, year) => {
  //   const winnerData = allWinners?.data.find(
  //     (w) => w.quarter === quarter && w.year === year
  //   );
  //   return winnerData ? winnerData.totalWinners : 0;
  // };
  if (error) return <p>Error loading winners</p>;
  return (
    <Grid item container>
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: variants.boxRadius.MAIN,
          height: '99.5%',
          overflow: 'hidden',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isSm ? (
          <Box sx={{ flex: 0.1, border: 0 }}>
            {isLoading ? (
              <>
                {/* <Skeleton
                  variant="rectangular"
                  width={'90%'}
                  height={'5vh'}
                  sx={{ marginTop: '1rem', marginRight: '1rem', marginLeft: '1rem' }}
                /> */}
                {/* <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '70vh',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.5)', // Adjust the transparency level
                    backdropFilter: 'blur(5px)', // Adds a blur effect to the background
                  }}
                >
                  <img src={GNXTLogo} style={{ height: 70, width: 150 }} />
                  <Typography sx={{ mt: 2 }}>Loading...</Typography>
                </Box> */}
                <LoadingScreen />
              </>
            ) : (
              <>
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    marginTop: '1rem',
                    marginRight: '1rem',
                    marginLeft: '1rem',
                    backgroundColor: 'white',
                  }}
                >
                  {years?.data && (
                    <TextField
                      fullWidth
                      select={true}
                      label="Select Year"
                      value={selectedYear}
                      onChange={handleYearChange}
                      variant="outlined"
                      size="small"
                    >
                      {years?.data.map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Grid>

                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    marginRight: '1rem',
                    marginLeft: '1rem',
                    backgroundColor: 'white',
                  }}
                >
                  <Tabs
                    value={selectedQuarter - 1}
                    onChange={handleQuarterChange}
                    variant="scrollable"
                    sx={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}
                  >
                    {quarters.map((quarter, index) => (
                      <Tab
                        key={index}
                        icon={icons[quarter]}
                        label={<Typography sx={{ fontSize: '10px' }}>{labels[quarter]}</Typography>}
                        sx={{
                          minWidth: 'auto',
                        }}
                      />
                    ))}
                  </Tabs>
                </Grid>

                {(!winnerData || !winnerData.data || winnerData.data.length === 0) && !isFetching ? (
                  <Grid
                    container
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      alignContent: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      height: `calc(100vh - 200px)`,
                      width: '100%',
                    }}
                  >
                    <img
                      src={Light}
                      alt={'RnR winners'}
                      sx={{ width: '90%', mixBlendMode: '-moz-initial', height: '100%' }}
                    />
                  </Grid>
                ) : (
                  <Grid sx={{ width: '95%', height: `calc(100vh - 270px)`, overflowY: 'scroll', margin: '0 auto' }}>
                    {winnerData?.data && !isFetching ? (
                      winnerData.data.map((w, i) => (
                        <SmallWinnerCard key={i} winner={w} setSelectedWinner={setSelectedWinner} setOpen={setOpen} />
                      ))
                    ) : (
                      <LoadingScreen />
                    )}
                  </Grid>
                )}
                <Can I={'create'} a={'RnR' || 'Shabash'}>
                  <SwipeEdge
                    height="70vh"
                    title="Nominate for RnR"
                    content={
                      <>
                        <RnRDrawer />
                      </>
                    }
                  />
                </Can>
              </>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 0.1, backgroundColor: 'white' }}>
            <Tabs
              value={selectedQuarter - 1}
              onChange={handleQuarterChange}
              sx={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}
            >
              {quarters.map((quarter, index) => (
                <Tab
                  key={index}
                  icon={icons[quarter]}
                  label={labels[quarter]}
                  sx={{
                    width: isSm ? 'auto' : '15%',
                    minWidth: 'auto',
                    padding: isMdUp ? '0 50px' : '',
                  }}
                />
              ))}

              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mr: 4, mt: 2, position: 'sticky' }}>
                {years?.data && (
                  <TextField
                    select={true}
                    label="Select Year"
                    value={selectedYear}
                    onChange={handleYearChange}
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 150 }}
                  >
                    {years?.data.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <Can I={'create'} a={'RnR' || 'Shabash'}>
                  <Box sx={{ mx: 2 }}>
                    <Button
                      disabled={drawerOpen}
                      variant="contained"
                      sx={{ fontWeight: 'bold', borderRadius: '8px', fontSize: variants.fontSize.TEXT }}
                      onClick={() => {
                        navigate('/home/rnr/winners/rnrNomination');
                      }}
                    >
                      Nominate
                    </Button>
                  </Box>
                </Can>
              </Grid>
            </Tabs>
            <Grid item container sx={{ flex: 1, backgroundColor: '#fff', p: 2, border: 0 }}>
              {(!winnerData || !winnerData.data || winnerData.data.length === 0) && !isFetching ? (
                <Grid
                  container
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    alignContent: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    height: `calc(100vh - 200px)`,
                    width: '100%',
                  }}
                >
                  <img
                    src={Light}
                    alt={'RnR winners'}
                    sx={{ width: '90%', mixBlendMode: '-moz-initial', height: '100%' }}
                  />
                </Grid>
              ) : (
                <Stack
                  sx={{
                    height: isSm ? 'calc(100vh - 240px)' : 'calc(100vh - 200px)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                    width: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flex: 1,
                      flexDirection: isSm ? 'column' : 'row',
                      flexWrap: 'wrap',
                      width: 1,
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                    }}
                  >
                    {!isFetching && winnerData?.data ? (
                      winnerData.data.map((w, i) =>
                        isSm ? (
                          <SmallWinnerCard key={i} winner={w} setSelectedWinner={setSelectedWinner} setOpen={setOpen} />
                        ) : (
                          !isFetching && (
                            <WinnerCard key={i} winner={w} setSelectedWinner={setSelectedWinner} setOpen={setOpen} />
                          )
                        )
                      )
                    ) : (
                      <RnRCardsSkeleton />
                    )}
                  </Box>
                </Stack>
              )}
            </Grid>
          </Box>
        )}
        {(isLoading || isFetching) &&
          (isSm ? (
            <>
              <LoadingScreen />
            </>
          ) : (
            <RnRCardsSkeleton />
          ))}
      </Box>
      {selectedWinner && <WinnerDialog onClose={handleCloseDialog} open={open} data={selectedWinner} />}
    </Grid>
  );
};

export default Winners;